/**
 * useComposerPublicationActions
 * ---
 * Extracted from `src/pages/Composer.tsx` for size discipline. Owns the
 * publication lifecycle handlers wired to the Outbox panel and the
 * dispatch confirm/resend overlays:
 *
 *   - archive / restore / promote
 *   - dispatch (with pending-confirm sheet) + outbox "send"
 *   - resend-to-failed
 *
 * State setters are passed in so the page keeps owning the snapshot and
 * the overlay coordinates; the hook only owns the busy flags and the
 * per-publication selection maps that are local to dispatch UX.
 */
import { useCallback, useState } from "react";
import { toast } from "sonner";
import {
  archivePublication,
  markPublicationSent,
  promotePublicationToSource,
  restorePublicationToOutbox,
  runPublicationDispatch,
  type ContentBriefPublication,
  type ContentPublishingDispatch,
} from "@/lib/composerCore";
import type { Snapshot, PendingDispatch } from "./composerPageHelpers";

type Args = {
  workspaceId: string | null;
  snapshot: Snapshot;
  setSnapshot: React.Dispatch<React.SetStateAction<Snapshot>>;
  refresh: () => Promise<void> | void;
  setPendingDispatch: (v: PendingDispatch | null) => void;
  setDrawerDispatchId: (v: string | null) => void;
};

export const useComposerPublicationActions = ({
  workspaceId,
  snapshot,
  setSnapshot,
  refresh,
  setPendingDispatch,
  setDrawerDispatchId,
}: Args) => {
  const [dispatchConnectorByPub, setDispatchConnectorByPub] = useState<Record<string, string>>({});
  const [allowStaleByPub, setAllowStaleByPub] = useState<Record<string, boolean>>({});
  const [dispatchingPubId, setDispatchingPubId] = useState<string | null>(null);
  const [historyOpenByPub, setHistoryOpenByPub] = useState<Record<string, boolean>>({});
  const [resendingDispatchId, setResendingDispatchId] = useState<string | null>(null);

  const requestDispatchPublication = useCallback(
    (publicationId: string) => {
      if (!workspaceId) return;
      const connectorId = dispatchConnectorByPub[publicationId];
      if (!connectorId) {
        toast.error("Pick a connector first");
        return;
      }
      const pub = snapshot.briefPublications.find((p) => p.id === publicationId);
      const connector = snapshot.publishingConnectors.find((c) => c.id === connectorId);
      if (!pub || !connector) {
        toast.error("Connector or publication not found");
        return;
      }
      let recipientSummary: string | null = null;
      const cfg = connector.config as Record<string, unknown> | null;
      if (connector.connector_type === "email") {
        const recipients = Array.isArray(cfg?.recipients)
          ? (cfg!.recipients as unknown[]).filter((v): v is string => typeof v === "string")
          : [];
        recipientSummary = recipients.length
          ? `${recipients.length} recipient${recipients.length === 1 ? "" : "s"}: ${recipients.slice(0, 3).join(", ")}${recipients.length > 3 ? "…" : ""}`
          : "No recipients configured";
      } else if (connector.connector_type === "kit") {
        recipientSummary = "Creates a draft broadcast in Kit — you'll send from there.";
      } else if (connector.connector_type === "bento") {
        recipientSummary = "Creates a draft broadcast in Bento — you'll send from there.";
      } else if (connector.connector_type === "wordpress") {
        const site = typeof cfg?.site === "string" ? cfg.site : null;
        recipientSummary = site
          ? `Creates a draft post on ${site} — you'll publish from WordPress.com.`
          : "Creates a draft post on WordPress.com — you'll publish from there.";
      } else if (connector.connector_type === "webhook") {
        const url = typeof cfg?.url === "string" ? cfg.url : null;
        recipientSummary = url ? `POST → ${url}` : "Webhook URL configured";
      }
      setPendingDispatch({
        publicationId,
        publicationTitle: pub.prepared_title || "Untitled",
        destinationFormat: pub.destination_format,
        connectorId,
        connectorLabel: connector.display_name,
        connectorType: connector.connector_type,
        recipientSummary,
      });
    },
    [workspaceId, dispatchConnectorByPub, snapshot.briefPublications, snapshot.publishingConnectors, setPendingDispatch],
  );

  const handleDispatchPublication = useCallback(
    async (publicationId: string) => {
      if (!workspaceId) return;
      const connectorId = dispatchConnectorByPub[publicationId];
      if (!connectorId) {
        toast.error("Pick a connector first");
        return;
      }
      setDispatchingPubId(publicationId);
      try {
        const dispatch = await runPublicationDispatch(workspaceId, {
          publicationId,
          connectorId,
          allowStale: allowStaleByPub[publicationId] === true,
        });
        setSnapshot((prev) => ({
          ...prev,
          publishingDispatches: [dispatch, ...prev.publishingDispatches.filter((d) => d.id !== dispatch.id)],
        }));
        setHistoryOpenByPub((prev) => ({ ...prev, [publicationId]: true }));
        if (dispatch.status === "succeeded") {
          const pub = snapshot.briefPublications.find((p) => p.id === publicationId);
          if (pub) {
            try { await markPublicationSent(workspaceId, pub, dispatch.id); } catch { /* non-blocking */ }
          }
          toast.success("Sent to connector");
        } else if (dispatch.status === "failed") {
          toast.error(dispatch.error_message || "Dispatch failed");
        } else {
          toast.success("Dispatch queued");
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Dispatch failed";
        toast.error(message);
      } finally {
        setDispatchingPubId(null);
        setPendingDispatch(null);
      }
    },
    [workspaceId, dispatchConnectorByPub, allowStaleByPub, snapshot.briefPublications, setSnapshot, setPendingDispatch],
  );

  const handleResendToFailed = useCallback(
    async (dispatch: ContentPublishingDispatch, recipients: string[]) => {
      if (!workspaceId || !dispatch.publication_id || !dispatch.connector_id || recipients.length === 0) return;
      setResendingDispatchId(dispatch.id);
      try {
        const next = await runPublicationDispatch(workspaceId, {
          publicationId: dispatch.publication_id,
          connectorId: dispatch.connector_id,
          allowStale: true,
          recipientOverride: recipients,
        });
        setSnapshot((prev) => ({
          ...prev,
          publishingDispatches: [next, ...prev.publishingDispatches.filter((d) => d.id !== next.id)],
        }));
        setDrawerDispatchId(next.id);
        if (next.status === "succeeded") toast.success(`Resent to ${recipients.length} recipient${recipients.length === 1 ? "" : "s"}`);
        else if (next.status === "failed") toast.error(next.error_message || "Resend failed");
        else toast.success("Resend queued");
      } catch (error) {
        toast.error((error as Error).message ?? "Resend failed");
      } finally {
        setResendingDispatchId(null);
      }
    },
    [workspaceId, setSnapshot, setDrawerDispatchId],
  );

  const handleArchivePublication = useCallback(
    async (pub: ContentBriefPublication) => {
      if (!workspaceId) return;
      try {
        await archivePublication(workspaceId, pub);
        await refresh();
        toast.success("Moved to archive");
      } catch (error) {
        toast.error((error as Error).message ?? "Could not archive");
      }
    },
    [workspaceId, refresh],
  );

  const handleRestorePublication = useCallback(
    async (pub: ContentBriefPublication) => {
      if (!workspaceId) return;
      try {
        await restorePublicationToOutbox(workspaceId, pub);
        await refresh();
        toast.success("Restored to outbox");
      } catch (error) {
        toast.error((error as Error).message ?? "Could not restore");
      }
    },
    [workspaceId, refresh],
  );

  const handlePromotePublication = useCallback(
    async (pub: ContentBriefPublication, bucket: "primary" | "secondary") => {
      if (!workspaceId) return;
      const brief = snapshot.briefs.find((b) => b.id === pub.piece_id);
      try {
        await promotePublicationToSource(workspaceId, pub, bucket, brief?.title ?? "Brief");
        await refresh();
        toast.success(`Added to ${bucket} corpus`);
      } catch (error) {
        toast.error((error as Error).message ?? "Could not promote");
      }
    },
    [workspaceId, snapshot.briefs, refresh],
  );

  const handleSendFromOutbox = useCallback(
    async (
      pub: ContentBriefPublication,
      connectorId: string,
      options?: { silent?: boolean },
    ): Promise<boolean> => {
      if (!workspaceId) return false;
      const silent = options?.silent === true;
      setDispatchingPubId(pub.id);
      try {
        const dispatch = await runPublicationDispatch(workspaceId, {
          publicationId: pub.id,
          connectorId,
          allowStale: true,
        });
        setSnapshot((prev) => ({
          ...prev,
          publishingDispatches: [dispatch, ...prev.publishingDispatches.filter((d) => d.id !== dispatch.id)],
        }));
        const ok = dispatch.status === "succeeded";
        if (ok) {
          await markPublicationSent(workspaceId, pub, dispatch.id);
          if (!silent) toast.success("Sent");
        } else if (dispatch.status === "failed") {
          if (!silent) toast.error(dispatch.error_message || "Send failed");
        } else if (!silent) {
          toast.success("Send queued");
        }
        await refresh();
        return ok || dispatch.status !== "failed";
      } catch (error) {
        if (!silent) toast.error((error as Error).message ?? "Send failed");
        return false;
      } finally {
        setDispatchingPubId(null);
      }
    },
    [workspaceId, setSnapshot, refresh],
  );

  return {
    // state
    dispatchConnectorByPub,
    setDispatchConnectorByPub,
    allowStaleByPub,
    setAllowStaleByPub,
    dispatchingPubId,
    historyOpenByPub,
    setHistoryOpenByPub,
    resendingDispatchId,
    // handlers
    requestDispatchPublication,
    handleDispatchPublication,
    handleResendToFailed,
    handleArchivePublication,
    handleRestorePublication,
    handlePromotePublication,
    handleSendFromOutbox,
  };
};
